import React, { Component } from 'react';
import { array, arrayOf, bool, func, object, oneOf, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { isAnyFilterActive, isMainSearchTypeKeywords, isOriginInUse } from '../../util/search';
import { parse } from '../../util/urlHelpers';
import { USER_ROLE_MERCHANT, propTypes } from '../../util/types';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { listingfilter } from '../../config/configListing';

import {
  Button,
  Footer,
  H3,
  H5,
  ListingCard,
  ModalInMobile,
  NamedLink,
  Page,
} from '../../components';


import TopbarContainer from '../TopbarContainer/TopbarContainer';

import { setActiveListing } from '../../containers/SearchPage/SearchPage.duck';
import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
} from '../../containers/SearchPage/SearchPage.shared';

import Slider from 'react-slick';

import FilterComponent from '../../containers/SearchPage/FilterComponent';
import MainPanelHeader from '../../containers/SearchPage/MainPanelHeader/MainPanelHeader';
import SearchFiltersPrimary from '../../containers/SearchPage/SearchFiltersPrimary/SearchFiltersPrimary';

import css from './SectionHeroPage.module.css';
import IconCollection from '../../components/IconCollection/IconCollection';
import TopbarSearchForm from '../TopbarContainer/Topbar/TopbarSearchForm/TopbarSearchForm';

// Primary filters have their content in dropdown-popup.
// With this offset we move the dropdown to the left a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

export class SectionHeroPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isSearchMapOpenOnMobile: props.tab === 'map',
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      isSecondaryFiltersOpen: false,
    };

    this.handleSubmit = this.handleSubmit.bind(this);

    // Filter functions
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);
  }

  handleSubmit(values) {
    const { currentSearchParams, params } = this.props;
    const { history, config, routeConfiguration } = this.props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };

    history.push(
      createResourceLocatorString('SearchPage', routeConfiguration, { ...params }, searchParams)
    );
  }

  getHandleChangedValueFn(useHistoryPush) {
    const { history, routeConfiguration, config, params } = this.props;
    // const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        return {
          currentQueryParams: {
            ...mergedQueryParams,
            ...updatedURLParams,
            ...keywordsMaybe,
            address,
            bounds,
          },
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(
            searchParams,
            listingfilter,
            defaultFiltersConfig,
            sortConfig
          );
          let { type = 'shop-by-merchants' } = params;
          if (search.pub_category) {
            type = search.pub_category;
            search.pub_category = null;
          }
          history.push(
            createResourceLocatorString('SearchPage', routeConfiguration, { type }, search)
          );
        }
      };

      this.setState(updater, callback);
    };
  }

  render() {
    const {
      intl,
      listings,
      location,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams,
      routeConfiguration,
      config,
      isMapVariant,
      params,
    } = this.props;
    
    const productListing =
      listings.length && listings.filter(st => st?.attributes?.publicData?.category?.toLowerCase() == 'products');
    const productListingHasStock =
      productListing && productListing.filter(st => st?.currentStock?.attributes?.quantity > 0);      
    const serviceListing =
      listings?.length &&
      listings.filter(st => st?.attributes?.publicData?.category?.toLowerCase() === 'services');
    const merchantsListing =
      listings?.length &&
      listings.filter(st => st?.attributes?.publicData?.listingType === USER_ROLE_MERCHANT);
    const { address, origin, bounds } = parse(location?.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    // const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;

    const topbarSearcInitialValues = () => {
      // Only render current search if full place object is available in the URL params
      const locationFieldsPresent = isOriginInUse(config)
        ? address && origin && bounds
        : address && bounds;
      return {
        location: locationFieldsPresent
          ? {
              search: address,
              selectedPlace: { address, origin, bounds },
            }
          : null,
      };
    };

    const initialSearchFormValues = topbarSearcInitialValues();

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      listingfilter,
      defaultFiltersConfig,
      sortConfig,
      isOriginInUse(config)
    );

    const validQueryParams = validFilterParams(
      searchParamsInURL,
      listingfilter,
      defaultFiltersConfig,
      false
    );

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingfilter,
      activeListingTypes
    );
    const availablePrimaryFilters = [
      ...customPrimaryFilters,
      // ...defaultFilters
    ];

    const hasSecondaryFilters = !!(customSecondaryFilters && customSecondaryFilters.length > 0);

    // Selected aka active filters
    const selectedFilters = validFilterParams(
      validQueryParams,
      listingfilter,
      defaultFiltersConfig
    );

    // Selected aka active secondary filters
    const selectedSecondaryFilters = hasSecondaryFilters
      ? validFilterParams(validQueryParams, customSecondaryFilters, [])
      : {};
    const selectedSecondaryFiltersCount = Object.keys(selectedSecondaryFilters).length;
    const propsForSecondaryFiltersToggle = hasSecondaryFilters
      ? {
          isSecondaryFiltersOpen: this.state.isSecondaryFiltersOpen,
          toggleSecondaryFiltersOpen: isOpen => {
            this.setState({ isSecondaryFiltersOpen: isOpen, currentQueryParams: {} });
          },
          selectedSecondaryFiltersCount,
        }
      : {};

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    // const conflictingFilterActive = isAnyFilterActive(
    //   sortConfig.conflictingFilters,
    //   validQueryParams,
    //   listingFieldsConfig,
    //   defaultFiltersConfig
    // );

    // const { bounds, origin } = searchParamsInURL || {};
    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>

    const cardRenderSizes = isMapVariant => {
      if (isMapVariant) {
        // Panel width relative to the viewport
        const panelMediumWidth = 50;
        const panelLargeWidth = 62.5;
        return [
          '(max-width: 767px) 100vw',
          `(max-width: 1023px) ${panelMediumWidth}vw`,
          `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
          `${panelLargeWidth / 3}vw`,
        ].join(', ');
      } else {
        // Panel width relative to the viewport
        const panelMediumWidth = 50;
        const panelLargeWidth = 62.5;
        return [
          '(max-width: 549px) 100vw',
          '(max-width: 767px) 50vw',
          `(max-width: 1439px) 26vw`,
          `(max-width: 1920px) 18vw`,
          `14vw`,
        ].join(', ');
      }
    };
    var settings = {
      dots: false,
      infinite: false,
      arrows: true,
      speed: 500,
      slidesToShow: 4,
      slidesToScroll: 1,
      initialSlide: 0,
      responsive: [
        {
          breakpoint: 1260,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 3,
          },
        },
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 3,
            infinite: true,
            dots: false,
            arrows: false,
          },
        },
        {
          breakpoint: 600,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 2,
            initialSlide: 2,
            arrows: false,
          },
        },
        {
          breakpoint: 480,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            infinite: true,
            arrows: false,
          },
        },
      ],
    };
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer
          className={topbarClasses}
          currentPage="SearchPage"
          currentSearchParams={urlQueryParams}
        />
        <div className={css.container}>
          <div className={css.searchResultContainer}>
            <MainPanelHeader
              className={css.mainPanelMapVariant}
              listingsAreLoaded={listingsAreLoaded}
              resultsCount={totalItems}
              searchInProgress={searchInProgress}
              searchListingsError={searchListingsError}
            >
              <SearchFiltersPrimary {...propsForSecondaryFiltersToggle}>
                {availablePrimaryFilters.map(config => {
                  return (
                    <FilterComponent
                      key={`SearchFiltersPrimary.${config.key}`}
                      idPrefix="SearchFiltersPrimary"
                      config={config}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      params={params}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      showAsPopup
                      contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
                    />
                  );
                })}
              </SearchFiltersPrimary>
            </MainPanelHeader>
            {/* <div className={css.searchBarBox}>
              <TopbarSearchForm
                className={css.searchLink}
                desktopInputRoot={css.topbarSearchWithLeftPadding}
                onSubmit={this.handleSubmit}
                initialValues={initialSearchFormValues}
                appConfig={config}
              />
            </div> */}
          </div>
          <div className={css.landingFilterWrapper}>
            <div className={css.liveLoughWrapper}>
              <h2>Live, laugh, shop with LuLocal</h2>
              <h4>Finally, shopping local is simple</h4>
              <p>
              <FormattedMessage id="SectionHeroPage.titleHeding" />
              </p>
            </div>
            <div className={css.sectionWrapper}>
              <h2> Say hello to..</h2>
              <p>Discover your new go-to from our LuLocal merchants</p>
              <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
                <Slider {...settings}>
                  {merchantsListing &&
                    Array.isArray(merchantsListing) &&
                    merchantsListing.map(l => (
                      <ListingCard
                        // className={css.listingCard}
                        key={l?.id?.uuid}
                        listing={l}
                        renderSizes={cardRenderSizes(isMapVariant)}
                        setActiveListing={setActiveListing}
                      />
                    ))}
                </Slider>
              </div>
              <div className={css.showMoreSec}>
                <NamedLink name="SearchPage" params={{ type: 'shop-by-merchants' }}>
                  <Button className={css.learnMore}>
                    Shop all merchants
                    <IconCollection name="RIGHT_ARROW" />
                  </Button>
                </NamedLink>
              </div>
            </div>
            {/* sectionWrapper */}
            <div className={css.sectionWrapper}>
              <h2>Local Shopping</h2>
              <h2>just got a glow up</h2>
              <p>
                <FormattedMessage id="SectionHeroPage.fromTo" />
              </p>
              <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
                <Slider {...settings}>
                  {productListingHasStock &&
                    Array.isArray(productListingHasStock) &&
                    productListingHasStock.map(l => (
                      <ListingCard
                        className={css.listingCard}
                        key={l?.id?.uuid}
                        listing={l}
                        renderSizes={cardRenderSizes(isMapVariant)}
                        setActiveListing={setActiveListing}
                        marketplaceCurrency={marketplaceCurrency}
                      />
                    ))}
                </Slider>
              </div>
              <div className={css.showMoreSec}>
                <NamedLink name="SearchPage" params={{ type: 'products' }}>
                  <Button className={css.learnMore}>
                    <FormattedMessage id="LandingPage.ShowMoreProducts" />
                    <IconCollection name="RIGHT_ARROW" />
                  </Button>
                </NamedLink>
              </div>
            </div>
            <div className={css.sectionWrapper}>
              <h2>Find Trusted Local</h2>
              <h2> services that work for you</h2>
              <p>Explore new services near you</p>

              <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
                <Slider {...settings}>
                  {serviceListing &&
                    Array.isArray(serviceListing) &&
                    serviceListing.map(l => (
                      <ListingCard
                        serviceListing={serviceListing}
                        className={css.listingCard}
                        key={l?.id?.uuid}
                        listing={l}
                        renderSizes={cardRenderSizes(isMapVariant)}
                        setActiveListing={setActiveListing}
                      />
                    ))}
                </Slider>
              </div>
              <div className={css.showMoreSec}>
                <NamedLink name="SearchPage" params={{ type: 'services' }}>
                  <Button className={css.learnMore}>
                    <FormattedMessage id="LandingPage.ShowMoreServices" />
                    <IconCollection name="RIGHT_ARROW" />
                  </Button>
                </NamedLink>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </Page>
    );
  }
}

SectionHeroPageComponent.defaultProps = {
  listings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
  tab: 'listings',
  activeListingId: null,
};

SectionHeroPageComponent.propTypes = {
  listings: array,
  onActivateListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,
  tab: oneOf(['filters', 'listings', 'map']).isRequired,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <SectionHeroPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
  } = state.SectionHeroPage;

  const listings = getListingsById(state, currentPageResultIds);

  return {
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onActivateListing: listingId => dispatch(setActiveListing(listingId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SectionHeroPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedSearchPage);

export default SectionHeroPage;
